import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { get, union } from 'lodash';
import { ToastrService } from '@bssh/comp-lib';
import { ModalService } from '@app/shared/modals/services/modal.service';
import {
  FileToReplace,
  ReferenceFileApiService
} from '@app/resources/reference-file/service/reference-file-api.service';
import { ReferenceFileMapperService } from '@app/core/services/mapper/reference-file-mapper.service';
import { ReferenceFilesService } from '@stratus/gss-ng-sdk';
import { SharedSpinnerService } from '@app/run-planning/services/shared-spinner/shared-spinner.service';
import { GssApiCacheService } from '@app/run-planning/services/gss-api-cache-service';
import { DirectUploadInfo } from '@app/shared/ica-file-upload-service';
import { IReferenceFile } from '@models/settings/reference-file';
import {
  IReferenceFileModalInput,
  IReferenceFileModalOutput,
  IAlertModalInput,
  IAlertModalOutput,
  IFileUploadModalInput,
  IFileUploadProgressModalOutput,
} from '@app/shared/modals/model/action-modal';
import {
  ReferenceFileModalComponent
} from '@app/shared/modals/custom-kits/reference-file-modal/reference-file-modal.component';
import { AlertModalType } from '@app/shared/modals/alert-modal/alert-modal-texts';
import { AlertModalComponent } from '@app/shared/modals/alert-modal/alert-modal.component';
import {
  FileUploadProgressModalComponent
} from '@app/shared/modals/file-upload-progress-modal/file-upload-progress-modal.component';
import { SubSink } from 'subsink';

export interface ReferenceFileUploadOptions {
  fileType: string;
  genomeFilterGenomeId?: string;
  genomeHashTableVersion?: string;
  analysisVersionDefinitionId?: string;
}

export interface ReferenceFileUploadResult {
  referenceFileId: string;
  fileName: string;
  displayName: string;
  dataLocationUri: string;
  supportedGenomeIds: string[];
}

@Injectable({ providedIn: 'root' })
export class ReferenceFileUploadOrchestratorService {

  private subs = new SubSink();

  constructor(
    private toastr: ToastrService,
    private modalService: ModalService,
    private referenceFileApiService: ReferenceFileApiService,
    private fileMapper: ReferenceFileMapperService,
    private referenceFilesService: ReferenceFilesService,
    private sharedSpinnerService: SharedSpinnerService,
    private gssApiCacheService: GssApiCacheService,
  ) {}

  /**
   * Orchestrates the full upload flow:
   * validate → duplicate check → S3 upload (with progress) → metadata modal.
   *
   * Returns the result if the user confirms the metadata modal, or null if
   * cancelled at any point.
   */
  async uploadReferenceFile(file: File, options: ReferenceFileUploadOptions): Promise<ReferenceFileUploadResult | null> {
    this.sharedSpinnerService.startSpinner();

    if (!this.isFileNameValid(file.name)) {
      this.toastr.error('Invalid name for imported reference file:'
        + ' Only alphanumerics, period, dash, and underscore characters are allowed.'
        + ' The name must start with alphanumeric, dash, or underscore.');
      this.sharedSpinnerService.stopSpinner();
      return null;
    }

    const existingFileDetail = await this.getExistingFileDetail(file.name);
    this.sharedSpinnerService.stopSpinner();

    if (existingFileDetail) {
      const willProceed = await this.showAlertModal('REPLACE_EXISTING_REFERENCE_FILE').toPromise();
      if (!willProceed) {
        return null;
      }
    }

    try {
      this.sharedSpinnerService.startSpinner();
      const directUploadInfo = await this.referenceFileApiService.getDirectUploadInfo();
      this.sharedSpinnerService.stopSpinner();
      this.openUploadProgressModal(file);

      let { dataLocationUri, fileName, displayName, referenceFileId, supportedGenomeIds }
        = await this.getReferenceFile(file, existingFileDetail, directUploadInfo, options.fileType);
      this.modalService.closeModal();

      if (options.genomeFilterGenomeId) {
        supportedGenomeIds = union(supportedGenomeIds, [options.genomeFilterGenomeId]);
      }

      const result = await this.openMetadataModal(
        dataLocationUri, fileName, displayName, referenceFileId,
        supportedGenomeIds, options
      );
      return result;
    } catch (err) {
      if (err.name === 'RequestAbortedError') {
        this.toastr.info('Upload cancelled.');
      } else {
        if (err.name === 'TimeoutError') {
          this.toastr.error('File failed to be imported due to timeout.');
        } else {
          const errorMessage = get(err, 'error.message', '');
          const errorDetails = get(err, 'error.details', []);
          if (errorMessage && errorDetails.length > 0
            && Object.values(errorDetails[0]).length > 0) {
            this.toastr.error(`${errorMessage}: ${Object.values(errorDetails[0])[0]}`);
          } else {
            this.toastr.error(`Failed to import file. ${errorMessage}`);
          }
        }
        this.modalService.closeModal();
      }
      return null;
    }
  }

  isFileNameValid(name: string): boolean {
    const matches = name.match(/^[\w\-]+[\w\-\.]*/);
    return matches && matches[0] === name;
  }

  private async getExistingFileDetail(filename: string): Promise<FileToReplace> {
    const existingFilesPage = await this.referenceFileApiService.getReferenceFiles(filename);
    let existingFileDetail;
    if (existingFilesPage.items && existingFilesPage.items.length > 0) {
      const existingFile = existingFilesPage.items[0];
      existingFileDetail = { name: existingFile.name, fileId: existingFile.id };
    }
    return existingFileDetail;
  }

  private showAlertModal(type: AlertModalType): Observable<boolean> {
    const modalInput: IAlertModalInput = { type };
    return this.modalService.openModal(modalInput, AlertModalComponent).confirm.pipe(
      map((modalOutput: IAlertModalOutput) => modalOutput.isProceed)
    );
  }

  private async getReferenceFile(
    importedFile: File, existingFileDetail: FileToReplace,
    directUploadInfo: DirectUploadInfo, fileType: string
  ): Promise<IReferenceFile> {
    const uploadedFile = await this.referenceFileApiService
      .uploadOrReplaceReferenceFile(importedFile, directUploadInfo, existingFileDetail, { type: fileType });
    const referenceFile = await this.referenceFilesService.getReferenceFile(
      { referenceFileId: uploadedFile.id }).toPromise();
    return this.fileMapper.mapApiItemsToDataSourceModel(referenceFile);
  }

  private openUploadProgressModal(file: File): void {
    const modalInput: IFileUploadModalInput = {
      fileName: file.name,
      totalSizeInBytes: file.size,
      uploadedSizeInBytes: 0,
    };

    this.subs.sink = this.modalService.openModal(modalInput, FileUploadProgressModalComponent).confirm.pipe(
      map((modalOutput: IFileUploadProgressModalOutput) => modalOutput.isCancel),
    ).subscribe((isCancel) => {
      if (isCancel) {
        this.referenceFileApiService.cancelUpload(file);
      }
    });

    this.subs.sink = this.referenceFileApiService.bytesLoaded$.subscribe(uploadedSize => {
      modalInput.uploadedSizeInBytes = uploadedSize;
      this.modalService.setData(modalInput);
    });
  }

  private openMetadataModal(
    dataLocationUri: string, fileName: string, displayName: string,
    referenceFileId: string, supportedGenomeIds: string[],
    options: ReferenceFileUploadOptions
  ): Promise<ReferenceFileUploadResult | null> {
    return new Promise(resolve => {
      const data: IReferenceFileModalInput = {
        fileType: options.fileType,
        dataLocationUri,
        fileName,
        displayName,
        referenceFileId,
        supportedGenomeIds,
      };
      if (options.analysisVersionDefinitionId) {
        data.analysisVersionDefinitionId = options.analysisVersionDefinitionId;
      }
      if (options.genomeHashTableVersion) {
        data.genomeHashTableVersion = options.genomeHashTableVersion;
      }

      this.subs.sink = this.modalService.openModal(data, ReferenceFileModalComponent).confirm.subscribe({
        next: (modalOutput: IReferenceFileModalOutput) => {
          if (modalOutput) {
            this.gssApiCacheService.clearReferenceFilesCache();
            resolve({
              referenceFileId: modalOutput.referenceFileId,
              fileName: modalOutput.fileName,
              displayName: modalOutput.displayName,
              dataLocationUri: modalOutput.dataLocationUri,
              supportedGenomeIds: modalOutput.supportedGenomeIds,
            });
          } else {
            resolve(null);
          }
        },
      });
    });
  }
}
